/**
 * Auth Provider & Context
 * Based on auth-system.md specification
 *
 * Features:
 * - Hydrates from initial state (no auth flicker)
 * - Subscribes to auth state changes, including every other tab's: supabase-js
 *   relays them between tabs itself (see "Auth State Subscription")
 * - Error boundary for graceful error handling
 * - Provides useAuth() hook and convenience hooks
 * - Retry logic for profile fetching (handles eventual consistency after signup)
 */

import {
  useEffect,
  useState,
  useCallback,
  useMemo,
  useRef,
  type ReactNode,
  Component,
  type ErrorInfo,
} from "react";
import type {
  AuthState,
  InitialAuthState,
  OAuthProvider,
} from "@/lib/supabase/types";
import { services } from "@/services/client-factory";
import { debugLog, debugWarn } from "@/utils/debug";
import { invalidateIdentityCaches } from "@/utils/identityCacheRegistry";
import {
  fetchStaffSession,
  type StaffSessionBootstrap,
} from "@/services/sessionBootstrap";
import { AuthContext, type AuthContextValue } from "./auth-context";
import { shouldSkipProfileFetchOnSignIn } from "./signupProfileFetchFlag";
import {
  clearViewAsRecord,
  hasViewAsRecord,
  readViewAsRecord,
  writeViewAsRecord,
  type ViewAsRecord,
} from "@/lib/supabase/viewAs";
import {
  enableViewAsActions,
  endViewAs as endViewAsSession,
} from "@/services/viewAsService";

// ============================================
// Helper Functions
// ============================================

/**
 * Retry profile fetch with exponential backoff
 * Used after signup when user record may not be immediately available
 *
 * The profile might not exist immediately after signup due to:
 * 1. Database trigger (handle_new_user) hasn't completed yet
 * 2. RLS policy evaluation timing
 * 3. UPSERT from OAuth provisioning not yet committed
 */
async function fetchProfileWithRetry(
  userId: string,
  isNewSignup: boolean = false,
): Promise<any> {
  const maxRetries = isNewSignup ? 8 : 3;
  const baseDelay = isNewSignup ? 300 : 500;

  // For new signups, add an initial delay to allow the database trigger to complete
  // This reduces the chance of the first attempt failing
  if (isNewSignup) {
    debugLog("Auth", "New signup detected, waiting for profile creation...");
    await new Promise((resolve) => setTimeout(resolve, 500));
  }

  for (let attempt = 0; attempt <= maxRetries; attempt++) {
    try {
      // Prefer the one-call bootstrap. It returns this user's es_users row
      // along with the role and permission keys PermissionsProvider is about
      // to ask for, and the memo behind it means that provider's request is
      // served from this one rather than starting its own chain.
      //
      // If it cannot answer, the per-table read does, in this same attempt. It
      // returns null when the database does not have the function, and throws
      // on any other failure — a revoked grant, a timeout, a 5xx. A throw used
      // to be retried here instead, which held the sign-in spinner for about
      // 3.5s and then left the profile null, though the plain read would have
      // worked. PermissionsProvider falls back the same way.
      //
      // A profile of null is a different thing — the signup race — and is
      // handled by the retry below exactly as before.
      let bootstrap: StaffSessionBootstrap | null = null;
      try {
        bootstrap = await fetchStaffSession(userId);
      } catch (bootstrapError) {
        debugWarn(
          "Auth",
          "Session bootstrap failed, falling back to the per-table read:",
          bootstrapError,
        );
      }
      const { data, error } = bootstrap
        ? { data: bootstrap.profile, error: null }
        : await services.auth.getUserProfile(userId);

      // If we got data, return immediately
      if (data) {
        debugLog("Auth", "Profile fetched successfully", { attempt, userId });
        return data;
      }

      // If data is null (user not found), retry for new signups
      if (!data && attempt < maxRetries) {
        const delay = baseDelay * Math.pow(2, Math.min(attempt, 4)); // Cap exponential growth
        debugLog(
          "Auth",
          `Profile not found, retrying in ${delay}ms (attempt ${attempt + 1}/${maxRetries})`,
        );
        await new Promise((resolve) => setTimeout(resolve, delay));
        continue;
      }

      if (error) {
        // Check if it's a retryable error (PGRST116 = no rows, 406 = Not Acceptable)
        const errorCode = (error as any)?.code;
        const errorStatus = (error as any)?.status;
        const errorMessage = String(error);

        const shouldRetry =
          errorCode === "PGRST116" ||
          errorStatus === 406 ||
          errorMessage.includes("PGRST116") ||
          errorMessage.includes("Not Acceptable") ||
          errorMessage.includes("no rows");

        if (shouldRetry && attempt < maxRetries) {
          const delay = baseDelay * Math.pow(2, Math.min(attempt, 4));
          debugLog(
            "Auth",
            `Profile fetch error (${errorCode || errorStatus}), retrying in ${delay}ms (attempt ${attempt + 1}/${maxRetries})`,
          );
          await new Promise((resolve) => setTimeout(resolve, delay));
          continue;
        }
        throw error;
      }

      // data is null but no error - return null
      return null;
    } catch (err) {
      if (attempt === maxRetries) {
        debugWarn("Auth", "Max retries reached, giving up profile fetch", err);
        throw err;
      }
      const delay = baseDelay * Math.pow(2, Math.min(attempt, 4));
      debugLog(
        "Auth",
        `Exception during profile fetch, retrying in ${delay}ms (attempt ${attempt + 1}/${maxRetries})`,
        err,
      );
      await new Promise((resolve) => setTimeout(resolve, delay));
    }
  }

  throw new Error("Failed to fetch profile after retries");
}

// ============================================
// Error Boundary
// ============================================

interface AuthErrorBoundaryProps {
  children: ReactNode;
  fallback?: ReactNode;
}

interface AuthErrorBoundaryState {
  hasError: boolean;
  error: Error | null;
}

class AuthErrorBoundary extends Component<
  AuthErrorBoundaryProps,
  AuthErrorBoundaryState
> {
  constructor(props: AuthErrorBoundaryProps) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error: Error): AuthErrorBoundaryState {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo): void {
    console.error("[AuthErrorBoundary] Error caught:", error, errorInfo);
  }

  render(): ReactNode {
    if (this.state.hasError) {
      if (this.props.fallback) {
        return this.props.fallback;
      }
      return (
        <div className="min-h-screen flex items-center justify-center bg-gray-50">
          <div className="text-center p-8">
            <h2 className="text-xl font-semibold text-gray-900 mb-2">
              Application Error
            </h2>
            <p className="text-gray-600 mb-4">
              Something went wrong while loading the app. Please refresh the
              page.
            </p>
            <button
              onClick={() => window.location.reload()}
              className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
            >
              Refresh Page
            </button>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}

// ============================================
// Context
// ============================================

interface AuthProviderProps {
  children: ReactNode;
  initialState?: InitialAuthState;
}

export function AuthProvider({ children, initialState }: AuthProviderProps) {
  /**
   * Whose profile the initial bootstrap already resolved.
   *
   * supabase-js fires INITIAL_SESSION as soon as we subscribe, and that
   * handler used to fetch the profile the bootstrap directly above it had
   * just fetched — the same es_users row, twice, on every single page load.
   * Recording the id lets the handler recognise its own bootstrap and only
   * refresh the session object.
   */
  const bootstrappedProfileUserIdRef = useRef<string | null>(null);

  // Initialize from initial state or default
  const [state, setState] = useState<AuthState>({
    user: initialState?.user ?? null,
    profile: initialState?.profile ?? null,
    session: initialState?.session ?? null,
    isLoading: !initialState,
    isAuthenticated: !!initialState?.user,
    error: null,
  });

  // ============================================
  // View As (Super Admin impersonation)
  // ============================================

  // Read once at mount. The Supabase client was already built from this same
  // record, so if it is present every request in this tab is already running as
  // the target — the provider is reporting that fact, not deciding it.
  const [viewAsRecord, setViewAsRecord] = useState<ViewAsRecord | null>(() =>
    readViewAsRecord()
  );

  // ============================================
  // Visibility Change Handler - Session Refresh
  // ============================================

  useEffect(() => {
    // Track when the tab was last active to avoid unnecessary refreshes
    let lastVisibilityChange = Date.now();
    const MIN_REFRESH_INTERVAL_MS = 5000; // Minimum 5 seconds between refreshes
    let isRefreshing = false; // Prevent concurrent refresh attempts

    const handleVisibilityChange = async () => {
      // Only refresh when becoming visible
      if (document.visibilityState !== "visible") {
        lastVisibilityChange = Date.now();
        return;
      }

      // Prevent concurrent refresh attempts
      if (isRefreshing) {
        console.log(
          "[AuthProvider] Visibility change: refresh already in progress",
        );
        return;
      }

      const timeSinceLastChange = Date.now() - lastVisibilityChange;
      lastVisibilityChange = Date.now();

      // Skip if the tab was hidden for less than the minimum interval
      if (timeSinceLastChange < MIN_REFRESH_INTERVAL_MS) {
        console.log(
          `[AuthProvider] Tab visible (hidden for ${timeSinceLastChange}ms) - skipping refresh (too recent)`,
        );
        return;
      }

      // Skip refresh for unauthenticated (public) users - there is no session to refresh
      if (!state.isAuthenticated) {
        return;
      }

      console.log(
        `[AuthProvider] Tab became visible after ${timeSinceLastChange}ms - refreshing session`,
      );

      isRefreshing = true;

      try {
        // Always try to refresh the session when returning to the tab
        // This ensures the token is valid and synced with the server
        const { data: refreshedSession, error: refreshError } =
          await services.auth.refreshSession();

        if (refreshError) {
          const errorMessage = String(refreshError);

          // AuthSessionMissingError is expected for unauthenticated users — not a real error
          if (errorMessage.includes("Auth session missing")) {
            isRefreshing = false;
            return;
          }

          console.warn(
            "[AuthProvider] Session refresh failed on visibility change:",
            refreshError,
          );

          // Check if it's an auth error that means we're logged out
          if (
            errorMessage.includes("refresh_token") ||
            errorMessage.includes("Invalid") ||
            errorMessage.includes("expired") ||
            errorMessage.includes("JWT")
          ) {
            console.log("[AuthProvider] Session invalid - clearing auth state");
            setState({
              user: null,
              profile: null,
              session: null,
              isLoading: false,
              isAuthenticated: false,
              error: null,
            });
          }
          isRefreshing = false;
          return;
        }

        if (refreshedSession) {
          console.log(
            "[AuthProvider] Session refreshed successfully on visibility change",
          );

          // Validate user with server
          const { data: user, error: userError } =
            await services.auth.getUser();

          if (userError || !user) {
            console.warn("[AuthProvider] User validation failed:", userError);

            // Only clear auth state for definitive auth errors, not network issues
            const errorMessage = String(userError || "");
            const isAuthError =
              errorMessage.includes("JWT") ||
              errorMessage.includes("session") ||
              errorMessage.includes("token") ||
              errorMessage.includes("Invalid") ||
              errorMessage.includes("expired") ||
              errorMessage.includes("401") ||
              errorMessage.includes("403");

            if (isAuthError) {
              console.log(
                "[AuthProvider] Auth error detected - clearing state",
              );
              setState({
                user: null,
                profile: null,
                session: null,
                isLoading: false,
                isAuthenticated: false,
                error: null,
              });
            } else {
              console.log("[AuthProvider] Non-auth error, keeping session");
              // Keep the existing session for non-auth errors (network issues, etc.)
              setState((prev) => ({
                ...prev,
                session: refreshedSession,
              }));
            }
            isRefreshing = false;
            return;
          }

          console.log("[AuthProvider] User validated successfully:", user.id);

          // Fetch profile if needed
          try {
            const profile = await fetchProfileWithRetry(user.id, false);
            setState({
              user,
              profile,
              session: refreshedSession,
              isLoading: false,
              isAuthenticated: true,
              error: null,
            });
          } catch (profileErr) {
            console.warn("[AuthProvider] Profile fetch failed:", profileErr);
            // Still update session and user even if profile fails
            setState((prev) => ({
              ...prev,
              user,
              session: refreshedSession,
              isAuthenticated: true,
              isLoading: false,
            }));
          }
        } else {
          // No session after refresh - user is logged out
          console.log(
            "[AuthProvider] No session after refresh - clearing auth state",
          );
          if (state.isAuthenticated) {
            setState({
              user: null,
              profile: null,
              session: null,
              isLoading: false,
              isAuthenticated: false,
              error: null,
            });
          }
        }
      } catch (err) {
        console.warn(
          "[AuthProvider] Error during visibility change session refresh:",
          err,
        );
      } finally {
        isRefreshing = false;
      }
    };

    // Also handle focus events for more immediate response
    const handleFocus = () => {
      // Debounce with visibility change
      if (document.visibilityState === "visible") {
        handleVisibilityChange();
      }
    };

    document.addEventListener("visibilitychange", handleVisibilityChange);
    window.addEventListener("focus", handleFocus);

    return () => {
      document.removeEventListener("visibilitychange", handleVisibilityChange);
      window.removeEventListener("focus", handleFocus);
    };
  }, [state.isAuthenticated, state.user?.id]);

  // ============================================
  // Online/Offline Handler - Session Recovery
  // ============================================

  useEffect(() => {
    const handleOnline = async () => {
      debugLog("AuthProvider", "Network came online - refreshing session");

      // If we're supposed to be authenticated, try to refresh the session
      if (state.isAuthenticated || state.user) {
        try {
          const { data: session, error } = await services.auth.refreshSession();

          if (error) {
            debugWarn(
              "AuthProvider",
              "Session refresh failed after coming online:",
              error,
            );
            return;
          }

          if (session) {
            debugLog("AuthProvider", "Session refreshed after coming online");
            const { data: user } = await services.auth.getUser();
            if (user) {
              try {
                const profile = await fetchProfileWithRetry(user.id, false);
                setState({
                  user,
                  profile,
                  session,
                  isLoading: false,
                  isAuthenticated: true,
                  error: null,
                });
              } catch {
                setState((prev) => ({ ...prev, session, user }));
              }
            }
          } else {
            debugLog(
              "AuthProvider",
              "No session after coming online - user may need to re-login",
            );
          }
        } catch (err) {
          debugWarn("AuthProvider", "Error refreshing session on online:", err);
        }
      }
    };

    window.addEventListener("online", handleOnline);

    return () => {
      window.removeEventListener("online", handleOnline);
    };
  }, [state.isAuthenticated, state.user]);

  // ============================================
  // Periodic Session Health Check
  // ============================================

  useEffect(() => {
    // Only run periodic checks if authenticated
    if (!state.isAuthenticated || !state.session) {
      return;
    }

    // Check session health every 5 minutes
    const HEALTH_CHECK_INTERVAL_MS = 5 * 60 * 1000;

    const checkSessionHealth = async () => {
      // Only check if the tab is visible
      if (document.visibilityState !== "visible") {
        return;
      }

      try {
        const { data: currentSession } = await services.auth.getSession();

        if (!currentSession) {
          debugLog(
            "AuthProvider",
            "Periodic check: Session not found - clearing auth state",
          );
          setState({
            user: null,
            profile: null,
            session: null,
            isLoading: false,
            isAuthenticated: false,
            error: null,
          });
          return;
        }

        // Check if session is close to expiring
        const expiresAt = currentSession.expires_at;
        const now = Math.floor(Date.now() / 1000);
        const timeUntilExpiry = expiresAt ? expiresAt - now : 0;
        const REFRESH_THRESHOLD_SECONDS = 600; // 10 minutes

        if (timeUntilExpiry < REFRESH_THRESHOLD_SECONDS) {
          debugLog(
            "AuthProvider",
            `Periodic check: Session expires in ${timeUntilExpiry}s - refreshing`,
          );
          const { data: refreshedSession } =
            await services.auth.refreshSession();
          if (refreshedSession) {
            setState((prev) => ({ ...prev, session: refreshedSession }));
          }
        }
      } catch (err) {
        debugWarn("AuthProvider", "Periodic session check failed:", err);
      }
    };

    const intervalId = setInterval(
      checkSessionHealth,
      HEALTH_CHECK_INTERVAL_MS,
    );

    return () => {
      clearInterval(intervalId);
    };
  }, [state.isAuthenticated, state.session]);

  // ============================================
  // Auth State Subscription
  // ============================================

  // This one subscription is also how a tab learns what happened in the
  // others. supabase-js posts every auth event it emits on a BroadcastChannel
  // named after its storage key, and each other tab's client re-emits it to
  // the listeners below. So another tab's sign-in, sign-out and token refresh
  // all arrive here, and so does the SIGNED_IN a client emits on every page
  // load, when it recovers the stored session.
  //
  // This provider used to relay the same events again on a channel of its
  // own, and re-sent each one it heard. A page load therefore came straight
  // back from every other open tab, and the loading tab answered each echo
  // with a second /auth/v1/user call that the auth client queued behind its
  // own, holding back the session bootstrap: about 180 ms, and one request,
  // per extra tab on every hard load. Nothing here posts to other tabs now.
  //
  // A view-as tab is not reached by any of this. Its client keeps the
  // impersonated session under a storage key of its own (createViewAsClient),
  // so supabase-js relays its events on a different channel from the real
  // tabs'. auth-provider.multitab.test.tsx runs the real client in several
  // tabs to pin all of it.
  //
  // The listener below is synchronous, and must stay so: see afterAuthLock.
  useEffect(() => {
    let unsubscribe: (() => void) | undefined;
    let isMounted = true;
    /**
     * How many times this tab has signed out. A profile read that started
     * before a sign-out must not land after it: it would sign the tab back in.
     */
    let signOuts = 0;

    /**
     * Runs a listener's Supabase work after supabase-js has let go of its
     * auth lock.
     *
     * supabase-js emits most auth events while it holds that lock: every token
     * refresh, updateUser(), setSession() and exchangeCodeForSession(). It
     * waits for every listener to return before it lets go. Every Supabase
     * request first asks for the session, and getSession() queues behind the
     * same lock (in-process, even with the no-op lock the client is given).
     * So a listener that awaits a request waits on the lock, which is waiting
     * on the listener.
     *
     * That froze any tab without a profile at its next token refresh: the
     * listener's profile read never went out, the refresh never returned,
     * and neither did any request after it, until a reload. So the listener
     * awaits nothing; it hands its reads to this, which starts them in a
     * later task, as supabase-js's own documentation advises.
     *
     * `isCurrent` says whether a result may still be applied: not once the
     * provider has unmounted, and not once the tab has signed out since the
     * event.
     */
    const afterAuthLock = (
      work: (isCurrent: () => boolean) => Promise<void>,
    ) => {
      const signOutsAtEvent = signOuts;
      const isCurrent = () => isMounted && signOuts === signOutsAtEvent;
      setTimeout(() => {
        if (isCurrent()) void work(isCurrent);
      }, 0);
    };

    const setupSubscription = async () => {
      // Get initial session
      const { data: session } = await services.auth.getSession();

      if (session) {
        const { data: user } = await services.auth.getUser();
        if (user) {
          try {
            // Fetch profile - RLS circular dependency now fixed
            const profile = await fetchProfileWithRetry(user.id, false);
            // Claim this user's profile so the INITIAL_SESSION handler, which
            // fires the moment we subscribe below, does not fetch it again.
            // Only on success: if the fetch failed we leave the claim unset so
            // that handler still gets its chance to resolve the profile.
            bootstrappedProfileUserIdRef.current = user.id;
            if (isMounted) {
              setState({
                user,
                profile,
                session,
                isLoading: false,
                isAuthenticated: true,
                error: null,
              });
            }
          } catch (err) {
            debugWarn("AuthProvider", "Could not fetch initial profile:", err);
            if (isMounted) {
              setState({
                user,
                profile: null,
                session,
                isLoading: false,
                isAuthenticated: true,
                error: null,
              });
            }
          }
        } else if (isMounted) {
          setState((prev) => ({ ...prev, isLoading: false }));
        }
      } else if (isMounted) {
        setState((prev) => ({ ...prev, isLoading: false }));
      }

      // Subscribe to auth changes. Not async: whatever this awaited, it would
      // await inside supabase-js's auth lock (see afterAuthLock).
      unsubscribe = await services.auth.onAuthStateChange(
        (event, session) => {
          console.log(
            "[AuthProvider] Auth state change:",
            event,
            session?.user?.id,
          );

          if (!isMounted) return;

          switch (event) {
            case "SIGNED_IN":
            case "TOKEN_REFRESHED":
              if (session?.user) {
                // Check if we should skip profile fetch (during signup flow).
                // Read as the event arrives, not in the deferred read below.
                if (shouldSkipProfileFetchOnSignIn() && event === "SIGNED_IN") {
                  console.log(
                    "[AuthProvider] Skipping profile fetch (signup in progress)",
                  );
                  // Just set the user and session; the OAuth callback sets
                  // the profile once es_oauth_provision has settled the role.
                  if (isMounted) {
                    setState({
                      user: session.user,
                      profile: null,
                      session,
                      isLoading: false,
                      isAuthenticated: true,
                      error: null,
                    });
                  }
                  return;
                }

                // OPTIMIZATION: If we're already authenticated with the same user,
                // just update the session without re-fetching the profile.
                // This prevents unnecessary profile fetches on every visibility change.
                setState((prevState) => {
                  if (
                    prevState.isAuthenticated &&
                    prevState.user?.id === session.user.id &&
                    prevState.profile
                  ) {
                    console.log(
                      "[AuthProvider] Already authenticated with same user, updating session only",
                    );
                    return {
                      ...prevState,
                      session,
                      user: session.user,
                    };
                  }
                  // Return previous state - we'll handle the profile fetch below
                  return prevState;
                });

                const signedIn = session;
                afterAuthLock(async (isCurrent) => {
                  // Check if we need to fetch profile (new user or missing profile)
                  // Use a ref-like check by getting current state
                  const currentState = await new Promise<AuthState>((resolve) => {
                    setState((prev) => {
                      resolve(prev);
                      return prev;
                    });
                  });

                  // Skip profile fetch if already authenticated with same user and have profile
                  if (
                    currentState.isAuthenticated &&
                    currentState.user?.id === signedIn.user.id &&
                    currentState.profile
                  ) {
                    // Every other tab's page load lands here too, as the
                    // SIGNED_IN supabase-js relays when that tab recovers the
                    // stored session. Nothing has changed for this tab, and
                    // nothing is sent back.
                    console.log(
                      "[AuthProvider] Profile already loaded, skipping fetch",
                    );
                    return;
                  }

                  console.log(
                    "[AuthProvider] Fetching profile for user:",
                    signedIn.user.id,
                  );
                  try {
                    // Fetch profile with retry for non-signup cases
                    const profile = await fetchProfileWithRetry(
                      signedIn.user.id,
                      false,
                    );
                    if (isCurrent()) {
                      setState({
                        user: signedIn.user,
                        profile,
                        session: signedIn,
                        isLoading: false,
                        isAuthenticated: true,
                        error: null,
                      });
                    }
                  } catch (err) {
                    console.warn("[AuthProvider] Could not fetch profile:", err);
                    if (isCurrent()) {
                      setState({
                        user: signedIn.user,
                        profile: null,
                        session: signedIn,
                        isLoading: false,
                        isAuthenticated: true,
                        error: null,
                      });
                    }
                  }
                });
              }
              break;

            case "SIGNED_OUT":
              signOuts += 1;
              invalidateIdentityCaches();
              // In a view-as tab this is the session ending, not a sign-out:
              // the target was deactivated, the sweep closed it, or the refresh
              // failed. Read sessionStorage directly rather than closing over
              // state, so an expired record still routes here.
              if (hasViewAsRecord()) {
                clearViewAsRecord();
                // Full navigation, not a React redirect: the tab must rebuild
                // its Supabase client from the Super Admin's own session, and
                // that decision is made once, at client construction.
                window.location.replace("/admin/users?view-as-ended=1");
                break;
              }

              if (isMounted) {
                setState({
                  user: null,
                  profile: null,
                  session: null,
                  isLoading: false,
                  isAuthenticated: false,
                  error: null,
                });
              }
              break;

            case "USER_UPDATED":
              // Emitted inside the lock by updateUser(), which ProfilePage
              // calls to change an email address.
              if (session?.user && isMounted) {
                const updated = session;
                afterAuthLock(async (isCurrent) => {
                  try {
                    const profile = await fetchProfileWithRetry(
                      updated.user.id,
                      false,
                    );
                    if (isCurrent()) {
                      setState((prev) => ({
                        ...prev,
                        user: updated.user,
                        profile,
                        session: updated,
                      }));
                    }
                  } catch (err) {
                    debugWarn(
                      "AuthProvider",
                      "Could not fetch profile on user update:",
                      err,
                    );
                  }
                });
              }
              break;

            case "PASSWORD_RECOVERY":
              // Handle password recovery flow
              debugLog("AuthProvider", "Password recovery initiated");
              break;

            // Handle INITIAL_SESSION event (fired on page load/refresh)
            default:
              // Log unknown events for debugging
              if (event !== "INITIAL_SESSION") {
                debugLog("AuthProvider", "Unhandled auth event:", event);
              } else {
                debugLog("AuthProvider", "Initial session event received");
                // For INITIAL_SESSION, update state if we have a session
                if (
                  session?.user &&
                  isMounted &&
                  bootstrappedProfileUserIdRef.current === session.user.id
                ) {
                  // The bootstrap immediately above already resolved this
                  // user's profile. Re-fetching it here read the same es_users
                  // row a second time on every page load. Take the session
                  // object — it is the fresher of the two — and keep the rest.
                  debugLog(
                    "AuthProvider",
                    "Initial session matches bootstrap, keeping profile",
                  );
                  setState((prev) => ({ ...prev, session }));
                } else if (session?.user && isMounted) {
                  // supabase-js 2.90 emits INITIAL_SESSION after letting go
                  // of its lock, but nothing promises that; defer it as well.
                  const initial = session;
                  afterAuthLock(async (isCurrent) => {
                    try {
                      const profile = await fetchProfileWithRetry(
                        initial.user.id,
                        false,
                      );
                      if (isCurrent()) {
                        setState({
                          user: initial.user,
                          profile,
                          session: initial,
                          isLoading: false,
                          isAuthenticated: true,
                          error: null,
                        });
                      }
                    } catch {
                      if (isCurrent()) {
                        setState({
                          user: initial.user,
                          profile: null,
                          session: initial,
                          isLoading: false,
                          isAuthenticated: true,
                          error: null,
                        });
                      }
                    }
                  });
                }
              }
              break;
          }
        },
      );
    };

    setupSubscription();

    return () => {
      isMounted = false;
      unsubscribe?.();
    };
  }, []);

  // ============================================
  // Auth Methods
  // ============================================

  const signInWithOAuth = useCallback(async (provider: OAuthProvider) => {
    setState((prev) => ({ ...prev, error: null }));

    const { data, error } = await services.auth.signInWithOAuth(provider, {
      redirectTo: `${window.location.origin}/auth/callback`,
    });

    if (error) {
      setState((prev) => ({ ...prev, error: error as any }));
      throw error;
    }

    // User will be redirected to OAuth provider
    if (data?.url) {
      window.location.href = data.url;
    }
  }, []);

  /**
   * Ends a view-as session and returns the tab to the Super Admin.
   *
   * Deliberately does NOT call supabase signOut. supabase-js defaults that to
   * `scope: "global"`, which revokes every refresh token the user holds — so
   * exiting a diagnosis session would sign the real person out on their own
   * phone and laptop. The server deletes exactly the one minted session
   * instead, by its recorded auth.sessions id.
   */
  const exitViewAs = useCallback(async () => {
    try {
      await endViewAsSession();
    } catch (error) {
      // Still leave. The cron sweep closes the row within minutes, and
      // stranding the admin inside someone else's account is far worse than a
      // ledger row that closes late.
      debugWarn("AuthProvider", "Could not end view-as session cleanly:", error);
    }
    clearViewAsRecord();
    window.location.replace("/admin/users?view-as-ended=1");
  }, []);

  /** Unblocks writes for the remainder of the session. Recorded server-side. */
  const enableViewAsWrites = useCallback(async () => {
    await enableViewAsActions();
    const current = readViewAsRecord();
    if (current) {
      const next: ViewAsRecord = { ...current, actionsEnabled: true };
      writeViewAsRecord(next);
      setViewAsRecord(next);
    }
  }, []);

  // Leave of our own accord at expiry. The server sweeps within five minutes,
  // but an access token already in hand stays valid to PostgREST until its own
  // one-hour expiry, so the client must not wait to be told.
  useEffect(() => {
    if (!viewAsRecord) return;

    const msLeft = Date.parse(viewAsRecord.expiresAt) - Date.now();
    if (msLeft <= 0) {
      void exitViewAs();
      return;
    }

    const timer = window.setTimeout(() => void exitViewAs(), msLeft);
    return () => window.clearTimeout(timer);
  }, [viewAsRecord, exitViewAs]);

  const signOut = useCallback(async () => {
    // In a view-as tab, "sign out" means "stop viewing as", never "sign the
    // target out". See exitViewAs for why the distinction is not cosmetic.
    if (hasViewAsRecord()) {
      await exitViewAs();
      return;
    }

    setState((prev) => ({ ...prev, error: null }));

    const { error } = await services.auth.signOut();

    if (error) {
      setState((prev) => ({ ...prev, error: error as any }));
      throw error;
    }

    setState({
      user: null,
      profile: null,
      session: null,
      isLoading: false,
      isAuthenticated: false,
      error: null,
    });

    // Navigation is handled by the caller or AuthGuard/ProtectedRoute components
  }, [exitViewAs]);

  const refreshProfile = useCallback(async () => {
    if (!state.user) return;

    const { data: profile } = await services.auth.getUserProfile(state.user.id);
    setState((prev) => ({ ...prev, profile }));
  }, [state.user]);

  const refreshSession = useCallback(async () => {
    const { data: session } = await services.auth.refreshSession();
    if (session) {
      setState((prev) => ({ ...prev, session }));
    }
  }, []);

  // ============================================
  // Memoized Context Value
  // ============================================

  const value = useMemo<AuthContextValue>(
    () => ({
      ...state,
      signInWithOAuth,
      signOut,
      refreshProfile,
      refreshSession,
      viewAs: viewAsRecord
        ? {
          record: viewAsRecord,
          exit: exitViewAs,
          enableActions: enableViewAsWrites,
        }
        : null,
    }),
    [
      state,
      signInWithOAuth,
      signOut,
      refreshProfile,
      refreshSession,
      viewAsRecord,
      exitViewAs,
      enableViewAsWrites,
    ],
  );

  return (
    <AuthErrorBoundary>
      <AuthContext.Provider value={value}>{children}</AuthContext.Provider>
    </AuthErrorBoundary>
  );
}
